import { isAcceptedCoverMatch } from '@/features/infringement/utils/acceptedCover'
import {
  ScoreLevel,
  type InfringementListQuery,
} from '@/shared/graphql/router/graphql'

export const FLAGGED_LEVELS = {
  [ScoreLevel.Low]: {
    label: 'Minimal',
    banner: 'bg-gray-300',
    border: 'border-gray-300',
    body: 'The compared work demonstrates a low overall similarity to your registered work. However, a low overall rating does not necessarily exclude the presence of isolated sections or elements with stronger similarities. A full technical report may provide additional insight into specific matches, detected patterns, and the relevance of individual similarities identified during analysis.',
  },
  [ScoreLevel.Medium]: {
    label: 'Moderate',
    banner: 'bg-yellow-500',
    border: 'border-yellow-500',
    body: 'The compared work demonstrates a moderate overall similarity to your registered work. While the file as a whole may not closely match, certain individual sections, elements, or components could still contain stronger similarities that warrant closer review. A full technical report can provide a detailed breakdown of potentially significant matches and supporting technical analysis.',
  },
  [ScoreLevel.High]: {
    label: 'Significant',
    banner: 'bg-red-500',
    border: 'border-red-500',
    body: 'A significant level of similarity was identified between the compared works across one or more elements. The detected patterns may indicate a strong likelihood of infringement or unauthorized derivative use. A full technical report is recommended to review detailed evidence, comparison analysis, and supporting findings.',
  },
} as const

export interface LevelPanel {
  label: string
  banner: string
  border: string
  text: string
  body: string
}

export const levelPanel = (level: ScoreLevel): LevelPanel => ({
  ...FLAGGED_LEVELS[level],
  text: 'text-white',
})

type Finding = InfringementListQuery['infringements']['edges'][number]['node']

export const findingPanel = (finding: Finding): LevelPanel => {
  if (isAcceptedCoverMatch(finding))
    return {
      label: 'Accepted cover match',
      banner: 'bg-green-700',
      border: 'border-green-700',
      text: 'text-white',
      body: 'The cover method accepted this registered work. Cover evidence has no overall similarity percentage. The report shows the method status and evidence.',
    }
  if (!finding.matches)
    return {
      label: 'Not assessed',
      banner: 'bg-gray-100',
      border: 'border-gray-200',
      text: 'text-gray-800',
      body: 'This finding does not include an overall similarity assessment. Create a report to compare the detected material with your registered work.',
    }
  const panel = levelPanel(finding.matches.level)
  if (finding.matches.score != null) return panel
  return {
    ...panel,
    body: 'This finding has a default risk level for review. It does not include an overall similarity score. Create a report to compare the detected material with your registered work.',
  }
}
